/* eslint-disable no-unused-vars */
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { GrowthService } from "../services/growth/GrowthService";
import { GrowthSettingsService } from "../services/growth/GrowthSettingsService";
import { ManagerAgent } from "../services/growth/agents/ManagerAgent";

const GrowthContext = createContext(null);

export const useGrowth = () => {
  const ctx = useContext(GrowthContext);
  if (!ctx) throw new Error("useGrowth must be used inside GrowthProvider");
  return ctx;
};

export const GrowthProvider = ({ children }) => {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [agentStatus, setAgentStatus] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [activeView, setActiveView] = useState("overview");

  const loadLeads = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const [data, userSettings] = await Promise.all([
        GrowthService.getLeads(user.uid),
        GrowthSettingsService.get(user.uid),
      ]);
      const prioritized = await ManagerAgent.prioritize(data);
      setLeads(prioritized);
      setSettings(userSettings);
      const m = await GrowthService.getMetrics(user.uid);
      setMetrics(m);
    } catch (err) {
      console.error("Growth load error:", err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const saveSettings = async (updates) => {
    const merged = { ...settings, ...updates };
    setSettings(merged);
    await GrowthSettingsService.save(user.uid, merged);
  };

  const addLead = async (data) => {
    const lead = await GrowthService.createLead(user.uid, data);
    setLeads((prev) => [lead, ...prev]);
    return lead;
  };

  const updateLead = async (leadId, updates) => {
    await GrowthService.updateLead(leadId, updates);
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, ...updates } : l))
    );
  };

  const deleteLead = async (leadId) => {
    await GrowthService.deleteLead(leadId);
    setLeads((prev) => prev.filter((l) => l.id !== leadId));
    if (selectedLead?.id === leadId) setSelectedLead(null);
  };

  const moveStage = async (leadId, stage) => {
    await GrowthService.updateStage(leadId, stage);
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, status: stage } : l))
    );
    // Auto-save winning outreach when a lead replies or converts
    if (stage === "replied" || stage === "converted") {
      const lead = leads.find((l) => l.id === leadId);
      if (lead?.outreach) {
        GrowthService.saveWinner(user.uid, lead).catch(() => {});
      }
    }
  };

  const setAgentRunning = (agentId, status) => {
    setAgentStatus((prev) => ({ ...prev, [agentId]: status }));
  };

  const refreshAfterAgent = async (leadId, updates) => {
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, ...updates } : l))
    );
  };

  const bulkImport = async (rows) => {
    const imported = await GrowthService.bulkImport(user.uid, rows);
    setLeads((prev) => [...imported, ...prev]);
    return imported;
  };

  return (
    <GrowthContext.Provider
      value={{
        leads,
        metrics,
        settings,
        loading,
        agentStatus,
        selectedLead,
        activeView,
        setSelectedLead,
        setActiveView,
        saveSettings,
        addLead,
        updateLead,
        deleteLead,
        moveStage,
        setAgentRunning,
        refreshAfterAgent,
        bulkImport,
        reload: loadLeads,
      }}
    >
      {children}
    </GrowthContext.Provider>
  );
};
